import type { ReactNode } from 'react';
import type { RichText } from '@cw/shared';
import { cn } from '@/lib/utils';

type Node = RichText;

/** Renderiza o JSON do editor como elementos React (sem HTML cru, sem editor carregado). */
function render(node: Node, key: number): ReactNode {
  const children = node.content?.map(render);
  switch (node.type) {
    case 'doc':
      return <>{children}</>;
    case 'paragraph':
      return <p key={key}>{children?.length ? children : <br />}</p>;
    case 'heading':
      return node.attrs?.level === 3 ? <h3 key={key}>{children}</h3> : <h2 key={key}>{children}</h2>;
    case 'bulletList':
      return <ul key={key}>{children}</ul>;
    case 'orderedList':
      return <ol key={key}>{children}</ol>;
    case 'listItem':
      return <li key={key}>{children}</li>;
    case 'blockquote':
      return <blockquote key={key}>{children}</blockquote>;
    case 'codeBlock':
      return <pre key={key}>{children}</pre>;
    case 'hardBreak':
      return <br key={key} />;
    case 'horizontalRule':
      return <hr key={key} />;
    case 'text': {
      let el: ReactNode = node.text;
      for (const m of node.marks ?? []) {
        if (m.type === 'bold') el = <strong>{el}</strong>;
        else if (m.type === 'italic') el = <em>{el}</em>;
        else if (m.type === 'strike') el = <s>{el}</s>;
        else if (m.type === 'code') el = <code>{el}</code>;
        else if (m.type === 'link' && m.attrs?.href && /^(https?:|mailto:)/i.test(m.attrs.href))
          el = (
            <a href={m.attrs.href} target="_blank" rel="noreferrer">
              {el}
            </a>
          );
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return null;
  }
}

export function RichView({ doc, className }: { doc: RichText | null; className?: string }) {
  if (!doc) return null;
  return (
    <div
      className={cn(
        'text-[13.5px] leading-relaxed [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l-2 [&_blockquote]:border-line-strong [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-press [&_code]:px-1 [&_h2]:mt-3 [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
        className,
      )}
    >
      {render(doc, 0)}
    </div>
  );
}

/** Texto simples (com quebras de linha) → documento do editor. */
export function plainToDoc(text: string): RichText {
  return {
    type: 'doc',
    content: text
      .split('\n')
      .map((line) => ({ type: 'paragraph', ...(line ? { content: [{ type: 'text', text: line }] } : {}) })),
  };
}
